import React from 'react';
import { LayoutEnum } from '@app/common';
import Link from 'next/link';
import { useTheme } from 'styled-components';
import { cn } from '../../../../utils/tailwind';
import { createStableClassToken } from '../../../../utils/stable-class-token';
import { Badge } from '../../badge/badge';

type MenuItemProps = {
    name: string;
    url?: string | null;
    icon?: React.ReactNode;
    layout?: LayoutEnum;
    isActive?: boolean;
    disabled?: boolean;
    badge?: string | null;
    onClick?: () => void;
    id?: string;
};

export const MenuItem: React.FC<MenuItemProps> = ({
    layout = LayoutEnum.Horizontal,
    name,
    url = null,
    icon,
    onClick,
    badge,
    isActive = false,
    disabled = false,
    id,
}: MenuItemProps) => {
    const theme = useTheme();
    const classToken = createStableClassToken('menu-item', [layout, isActive, disabled]);

    const handleOnClick = () => {
        if (disabled) {
            return;
        }

        if (onClick) {
            onClick();
        }
    };

    const linkClassName = cn(
        classToken,
        'relative flex items-center font-bold transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        layout === LayoutEnum.Horizontal && 'vl:max:px-medium vl:max:py-[1.6rem] vl:px-medium vl:py-[1.3rem]',
        layout === LayoutEnum.Vertical &&
            'vl:max:px-medium vl:max:py-[1.6rem] vl:mx-[0.3rem] vl:px-[0.5rem] vl:py-[1.6rem] xl:py-[2.9rem] xxl:mx-[2rem] vl:flex-col xxl:flex-row vl:xl:max:text-small',
        layout === LayoutEnum.Vertical &&
            isActive &&
            'vl:before:absolute vl:before:bottom-0 vl:before:left-0 vl:before:right-0 vl:before:h-[0.2rem] vl:before:content-[""]',
    );

    const iconWrapperClassName = cn(layout === LayoutEnum.Vertical && 'vl:flex vl:items-center vl:gap-[0.3rem]');

    const badgeClassName = cn(
        'absolute bottom-[-0.5rem] left-[5rem] ml-[1rem]',
        layout === LayoutEnum.Horizontal && 'vl:bottom-[-1rem] vl:left-[5.5rem] vl:ml-0',
        layout === LayoutEnum.Horizontal && isActive && 'vl:hidden',
        layout === LayoutEnum.Vertical &&
            'vl:static vl:bottom-auto vl:left-auto vl:ml-0 xxl:absolute xxl:bottom-[1.2rem] xxl:right-0',
    );

    const styleElement = (
        <style>{`
                .${classToken} .app-icon {
                    color: #8e89c0;
                    width: 2.2rem;
                    height: 2.2rem;
                    margin-right: 1rem;
                }

                .${classToken}:hover {
                    color: ${theme.palette.common.text};
                }

                .${classToken}:hover .app-icon {
                    color: ${theme.palette.common.text};
                }

                @media ${theme.breakpoints.vl.min} {
                    .${classToken}.menu-vertical .app-icon {
                        width: 1.8rem;
                        height: 1.8rem;
                        margin-right: 0;
                    }
                }

                @media ${theme.breakpoints.vl.min} and ${theme.breakpoints.xl.max} {
                    .${classToken}.menu-vertical .app-icon {
                        width: 1.6rem;
                        height: 1.6rem;
                    }
                }

                @media ${theme.breakpoints.xxl.min} {
                    .${classToken}.menu-vertical .app-icon {
                        width: 2.2rem;
                        height: 2.2rem;
                        margin-right: 1rem;
                    }
                }

                @media ${theme.breakpoints.vl.max} {
                    .${classToken}.menu-horizontal.menu-active,
                    .${classToken}.menu-vertical.menu-active {
                        background: ${theme.palette.gradient.primary.main};
                        color: ${theme.palette.color.white.main};
                    }

                    .${classToken}.menu-horizontal.menu-active .app-icon,
                    .${classToken}.menu-vertical.menu-active .app-icon {
                        color: inherit;
                    }
                }

                @media ${theme.breakpoints.vl.min} {
                    .${classToken}.menu-horizontal.menu-active {
                        background: ${theme.palette.gradient.primary.main};
                        color: ${theme.palette.color.white.main};
                    }

                    .${classToken}.menu-horizontal.menu-active .app-icon {
                        color: inherit;
                    }

                    .${classToken}.menu-vertical.menu-active::before {
                        background: var(--menu-item-underline);
                    }
                }
            `}</style>
    );

    const a = (
        <a
            onClick={() => {
                handleOnClick();
            }}
            id={id}
            className={linkClassName}
            style={{
                color: theme.palette.common.text,
                ...(layout === LayoutEnum.Vertical && isActive
                    ? { ['--menu-item-underline' as string]: theme.palette.gradient.primary.main }
                    : undefined),
            }}
        >
            <div className={iconWrapperClassName}>
                {icon}
                {badge ? (
                    <div className={badgeClassName}>
                        <Badge variant="negative" size="small">
                            {badge}
                        </Badge>
                    </div>
                ) : null}
            </div>
            {name}
        </a>
    );

    const withLayoutClasses = React.cloneElement(a, {
        className: cn(
            a.props.className,
            layout === LayoutEnum.Horizontal ? 'menu-horizontal' : 'menu-vertical',
            isActive && 'menu-active',
        ),
    });

    if (url && !disabled) {
        return (
            <>
                {styleElement}
                <Link href={url} passHref>
                    {withLayoutClasses}
                </Link>
            </>
        );
    }

    return (
        <>
            {styleElement}
            {withLayoutClasses}
        </>
    );
};
